import Vue from 'vue'
import VueRouter, { RouteConfig } from 'vue-router'
import store from '@/store'
import { Tabs, TabSettingName } from '@/constants/appSettings'
import * as envType from '@/constants/envType'
import { setCordova } from '@/utils/window'
import { Platform } from '@/constants/app'
import { Storage } from '@/store/user/storage'

Vue.use(VueRouter)

const isLoggedIn = async () => {
  return !!(await Storage.token())
}
const getToken = () => localStorage.getItem('token')
const castPage = (param: string | (string | null)[]): number | undefined =>
  Number(param) > 0 ? Number(param) : undefined

const setViewPort = (setting: string) => {
  const element = document.querySelector("meta[name='viewport']")

  if (!element) {
    console.warn('Failed to Set up viewport')
    return
  }

  element.setAttribute('content', setting)

  Vue.$postMessage.setViewport(setting)
}

const VIEWPORT_PC = 'width=1200'
const VIEWPORT_SP = 'width=device-width,initial-scale=1'

const routes: Array<RouteConfig> = [
  {
    path: '/about',
    name: 'About',
    component: () => import('../views/About.vue')
  },
  // {
  //   path: '/room',
  //   name: 'room-top',
  //   component: () => import('../views/WebSocketSample.vue')
  // },
  // {
  //   path: '/room/:name',
  //   name: 'room',
  //   component: () => import('../views/WebSocketSampleRoom.vue')
  // },
  {
    path: '/status',
    name: 'status',
    component: () => import('../views/WebSocketStatus.vue')
  },
  {
    path: '/ble_sample',
    name: 'BleSample',
    component: () => import('@/views/BLESample.vue'),
    beforeEnter: (to, from, next) => {
      if (process.env.NODE_ENV !== envType.DEVELOPMENT) {
        next(false)
      } else {
        next()
      }
    }
  },
  // {
  //   path: '/online_status',
  //   name: 'onlineStatus',
  //   component: () => import('../views/WebSocketOnlineStatus.vue')
  // },
  // {
  //   path: '/call',
  //   name: 'call',
  //   component: () => import('../views/WebSocketCallStatus.vue')
  // },
  {
    path: '/reset_password',
    name: 'ResetPassword',
    component: () => import('../views/reset_password.vue'),
    beforeEnter: (to, from, next) => {
      setViewPort(VIEWPORT_SP)
      next()
    }
  },
  {
    path: '/password_reset',
    name: 'ResetPassword2',
    component: () => import('../views/reset_password2.vue'),
    beforeEnter: (to, from, next) => {
      setViewPort(VIEWPORT_SP)
      next()
    }
  },
  {
    path: '/login',
    name: 'PatientLogin',
    component: () => import('../views/patient/Login.vue'),
    beforeEnter: async (to, from, next) => {
      setViewPort(VIEWPORT_SP)
      if (await isLoggedIn()) {
        next({ name: 'PatientHome' })
        return
      }
      next()
    }
  },
  {
    path: '/w/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
    beforeEnter: async (to, from, next) => {
      if (await isLoggedIn()) {
        next({ name: 'Home' })
        return
      }
      setViewPort(VIEWPORT_SP)
      next()
    }
  },

  {
    path: '/hangup',
    name: 'Hangup',
    component: () => import('../views/VideoCallHangup.vue'),
    beforeEnter: (to, from, next) => {
      setViewPort(VIEWPORT_SP)
      next()
    }
  },
  {
    path: '/w',
    component: () => import('../views/Base.vue'),
    beforeEnter: (to, from, next) => {
      setViewPort(VIEWPORT_PC)
      if (
        to.name === 'CallReceive' ||
        to.name === 'CallDispatch' ||
        to.name === 'DispensationQR' ||
        to.name === 'PrescriptionQR' ||
        to.name === 'FileDownload' ||
        to.name === 'PdTabCreateInapp' ||
        to.name === 'ResetPassword' ||
        to.name === 'ResetPassword2'
      ) {
        next()
      }
      const token = getToken()
      if (!token) {
        next({ name: 'Login' })
        return
      }
      store.commit('user/setTokenToHeader', token)
      next()
    },
    children: [
      {
        path: '',
        name: 'Home',
        component: () => import('../views/Home.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next({ name: 'Schedules' })
        }
      },
      {
        path: 'hangup-inner',
        name: 'WorkerHangupInner',
        component: () => import('../views/VideoCallHangupInner.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'password_change',
        name: 'PasswordChange',
        component: () => import('../views/PasswordChange.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path:
          'filedownload/' +
          ':token/:refreshToken/:platformId/' +
          ':type/:patientId/:fileId/:fileName',
        name: 'FileDownload',
        component: () => import('../views/FileDownload.vue'),
        props: route => ({
          type: String(route.params.type),
          patientId: Number(route.params.patientId),
          fileId: Number(route.params.fileId),
          fileName: String(route.params.fileName)
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          store.commit('user/login', { token: token, refresh: refreshToken })
          const platformId = String(to.params.platformId) as Platform
          setCordova({ platformId })
          next()
        }
      },
      {
        path:
          'callreceive/' +
          ':token/:refreshToken/:platformId/:roomId/:pin/:secret/:videoEnable/' +
          ':fromuserId/:fromuserPatient/:fromuserLastname/:fromuserFirstname/' +
          ':fromocpCode/:fromocpName/:fromocpSName',
        name: 'CallReceive',
        component: () => import('../views/VideoCallReceive.vue'),
        props: route => ({
          roomId: String(route.params.roomId),
          pin: String(route.params.pin),
          secret: String(route.params.secret),
          videoEnable: route.params.videoEnable === 'true',
          fromUser: {
            id: Number(route.params.fromuserId),
            isPatient: route.params.fromuserPatient == 'true',
            isWorker: route.params.fromuserPatient == 'false',
            firstName: String(
              decodeURIComponent(route.params.fromuserFirstname)
            ),
            lastName: String(decodeURIComponent(route.params.fromuserLastname))
          },
          fromOccupation: {
            code: String(route.params.fromocpCode),
            name: String(decodeURIComponent(route.params.fromocpName)),
            shortName: String(decodeURIComponent(route.params.fromocpSName))
          }
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          store.commit('user/login', { token: token, refresh: refreshToken })
          const platformId = String(to.params.platformId) as Platform
          setCordova({ platformId })
          next()
        }
      },
      {
        path:
          'calldispatch/:token/:refreshToken/:platformId/:videoApiSecret/:toUsers',
        name: 'CallDispatch',
        component: () => import('../views/VideoCallDispatch.vue'),
        props: route => ({
          roomId: String(route.params.roomId),
          pin: String(route.params.pin),
          secret: String(route.params.secret),
          videoApiSecret: String(route.params.videoApiSecret),
          toUsers: JSON.parse(decodeURIComponent(String(route.params.toUsers)))
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          store.commit('user/login', { token: token, refresh: refreshToken })
          const platformId = String(to.params.platformId) as Platform
          setCordova({ platformId })
          next()
        }
      },
      {
        path: 'manage',
        name: 'Manage',
        component: () => import('../views/Manage.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'schedules',
        name: 'Schedules',
        component: () => import('../views/Schedules.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'manage/patient',
        name: 'ManagePatient',
        component: () => import('../views/ManagePatient.vue'),
        props: route => ({
          page: castPage(route.query.page),
          query: String(route.query.query || '')
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'manage/patient/add',
        name: 'ManagePatientAdd',
        component: () => import('../views/ManagePatientAdd.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'manage/patient/add/confirm',
        name: 'ManagePatientAddConfirm',
        component: () => import('../views/ManagePatientAddConfirm.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)

          if (from.name !== 'ManagePatientAdd') {
            return next(false)
          }
          next()
        }
      },
      {
        path: 'manage/patient/add/complete',
        name: 'ManagePatientAddComplete',
        component: () => import('../views/ManagePatientAddComplete.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)

          if (
            from.name !== 'ManagePatientAddConfirm' &&
            from.name !== 'ManagePatient'
          ) {
            return next(false)
          }
          next()
        }
      },
      {
        path: 'manage/invitation/:invitationId/:hash',
        name: 'Invitation',
        component: () => import('../views/Invitation.vue'),
        props: route => ({
          invitationId: Number(route.params.invitationId),
          hash: route.params.hash
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'labotest/import/confirm',
        name: 'LabotestImportConfirm',
        component: () => import('../views/LabotestImportConfirm.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'labotest/import/complete',
        name: 'LabotestImportComplete',
        component: () => import('../views/LabotestImportComplete.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'manage/archived_data',
        name: 'ManageArchivedData',
        component: () => import('../views/ManageArchivedData.vue'),
        props: route => ({
          page: castPage(route.query.page),
          query: String(route.query.query || '')
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'manage/team',
        name: 'ManageTeam',
        component: () => import('../views/ManageTeam.vue'),
        props: route => ({
          page: castPage(route.query.page)
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'manage/team/:patientId(\\d+)',
        name: 'ManageTeamDetail',
        component: () => import('../views/ManageTeamDetail.vue'),
        props: route => ({
          patientId: Number(route.params.patientId)
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'manage/organization',
        name: 'ManageOrganization',
        component: () => import('@/views/ManageOrganization.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'manage/round',
        name: 'ManageRound',
        component: () => import('../views/ManageRound.vue'),
        props: route => ({
          page: castPage(route.query.page)
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'schedules/event/:patientId(\\d+)',
        name: 'ScheduleComment',
        component: () => import('../views/ScheduleComment.vue'),
        props: route => ({
          patientId: Number(route.params.patientId),
          scheduleId: Number(route.query.schedule) || null
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'schedules/event/add/:patientId(\\d+)',
        name: 'ScheduleCreate',
        component: () => import('../views/ScheduleEdit.vue'),
        props: route => ({
          patientId: Number(route.params.patientId),
          routingType: 'create',
          preInputToken: route.query.preInputToken || ''
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'schedules/event/edit/:scheduleId(\\d+)',
        name: 'ScheduleEdit',
        component: () => import('../views/ScheduleEdit.vue'),
        props: route => ({
          scheduleId: Number(route.params.scheduleId),
          routingType: 'edit'
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'message',
        name: 'Message',
        component: () => import('@/components/message/Message.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path:
          'dispensation/qr/:token/:refreshToken/:platformId/:patientId/:regular',
        name: 'DispensationQR',
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          const platformId = String(to.params.platformId) as Platform
          store.commit('user/login', { token: token, refresh: refreshToken })
          setCordova({ platformId })
          const regular = String(to.params.regular)
          const patientId = String(to.params.patientId)
          next({
            name: Tabs[TabSettingName.dispensation].routeName,
            params: { patientId },
            query: { qr: 'true', regular }
          })
        }
      },
      {
        path: 'prescription/qr/:token/:refreshToken/:platformId/:patientId',
        name: 'PrescriptionQR',
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          const platformId = String(to.params.platformId) as Platform
          store.commit('user/login', { token: token, refresh: refreshToken })
          setCordova({ platformId })
          const patientId = String(to.params.patientId)
          next({
            name: Tabs[TabSettingName.prescription].routeName,
            params: { patientId },
            query: { qr: 'true' }
          })
        }
      },
      {
        path: 'pd/create/inapp/:patientId/:token/:refreshToken/:platformId/',
        name: 'PdTabCreateInapp',
        component: () => import('@/views/tabs/pd/PdTabCreateInapp.vue'),
        props: route => ({
          patientId: Number(route.params.patientId)
        }),
        beforeEnter: (to, from, next) => {
          console.log('PdTabCreateInapp')
          setViewPort(VIEWPORT_PC)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          store.commit('user/login', {
            token: token,
            refresh: refreshToken
          })
          const platformId = String(to.params.platformId) as Platform
          setCordova({ platformId })
          next()
        }
      },
      {
        path: 'patient/:patientId(\\d+)',
        component: () => import('@/views/tabs/TabFrame.vue'),
        props: route => ({
          patientId: Number(route.params.patientId)
        }),
        children: [
          // FIXME: コンポーネント未作成
          {
            path: 'basicInfo',
            name: Tabs[TabSettingName.basicInfo].routeName,
            component: () => import('@/views/tabs/BasicInfo.vue'),
            beforeEnter: (to, from, next) => {
              setViewPort(VIEWPORT_PC)
              next()
            }
          },
          {
            path: 'laboTest',
            name: Tabs[TabSettingName.laboTest].routeName,
            component: () => import('@/views/tabs/LaboTestTab.vue'),
            props: route => ({
              patientId: Number(route.params.patientId),
              year: route.query.year ? Number(route.query.year) : null
            }),
            beforeEnter: (to, from, next) => {
              setViewPort(VIEWPORT_PC)
              next()
            }
          },
          {
            path: 'vitalSign',
            name: Tabs[TabSettingName.vitalSign].routeName,
            component: () => import('@/views/tabs/VitalsignTab.vue'),
            props: route => ({
              patientId: Number(route.params.patientId),
              year: route.query.year ? Number(route.query.year) : null,
              month: route.query.month ? Number(route.query.month) : null,
              day: route.query.day ? Number(route.query.day) : null,
              span: route.query.span ? String(route.query.span) : null,
              before: route.query.before ? String(route.query.before) : 'true'
            }),
            beforeEnter: (to, from, next) => {
              setViewPort(VIEWPORT_PC)
              next()
            }
          },
          {
            path: 'ecg',
            name: Tabs[TabSettingName.ecg].routeName,
            component: () => import('@/views/tabs/ECGTab.vue'),
            beforeEnter: (to, from, next) => {
              setViewPort(VIEWPORT_PC)
              next()
            }
          },
          {
            path: 'dicom',
            name: Tabs[TabSettingName.dicom].routeName,
            component: () => import('@/views/tabs/DicomTab.vue'),
            children: [
              {
                path: '/',
                name: Tabs[TabSettingName.dicom].routeName + 'Index',
                component: () => import('@/views/tabs/dicomTabs/Index.vue'),
                beforeEnter: (to, from, next) => {
                  setViewPort(VIEWPORT_PC)
                  next()
                }
              },
              {
                path: 'upload',
                name: Tabs[TabSettingName.dicom].routeName + 'Upload',
                component: () => import('@/views/tabs/dicomTabs/Upload.vue'),
                beforeEnter: (to, from, next) => {
                  setViewPort(VIEWPORT_PC)
                  next()
                }
              },
              {
                path: 'edit/:dicomId(\\d+)',
                name: Tabs[TabSettingName.dicom].routeName + 'Edit',
                component: () => import('@/views/tabs/dicomTabs/Edit.vue'),
                beforeEnter: (to, from, next) => {
                  setViewPort(VIEWPORT_PC)
                  next()
                }
              }
            ]
          },
          {
            path: 'image',
            name: Tabs[TabSettingName.image].routeName,
            component: () => import('@/views/tabs/ImageTab.vue'),
            children: [
              {
                path: '/',
                name: Tabs[TabSettingName.image].routeName + 'Index',
                component: () => import('@/views/tabs/imageTabs/Index.vue'),
                beforeEnter: (to, from, next) => {
                  setViewPort(VIEWPORT_PC)
                  next()
                }
              },
              {
                path: 'upload',
                name: Tabs[TabSettingName.image].routeName + 'Upload',
                component: () => import('@/views/tabs/imageTabs/Upload.vue'),
                beforeEnter: (to, from, next) => {
                  setViewPort(VIEWPORT_PC)
                  next()
                }
              },
              {
                path: 'edit/:imageId(\\d+)',
                name: Tabs[TabSettingName.image].routeName + 'Edit',
                component: () => import('@/views/tabs/imageTabs/Edit.vue'),
                beforeEnter: (to, from, next) => {
                  setViewPort(VIEWPORT_PC)
                  next()
                }
              }
            ]
          },
          {
            path: 'document',
            name: Tabs[TabSettingName.document].routeName,
            component: () => import('@/views/tabs/DocumentTab.vue'),
            children: [
              {
                path: '/',
                name: Tabs[TabSettingName.document].routeName + 'Index',
                component: () => import('@/views/tabs/documentTabs/Index.vue'),
                beforeEnter: (to, from, next) => {
                  setViewPort(VIEWPORT_PC)
                  next()
                }
              },
              {
                path: 'upload',
                name: Tabs[TabSettingName.document].routeName + 'Upload',
                component: () => import('@/views/tabs/documentTabs/Upload.vue'),
                beforeEnter: (to, from, next) => {
                  setViewPort(VIEWPORT_PC)
                  next()
                }
              },
              {
                path: 'edit/:documentId(\\d+)',
                name: Tabs[TabSettingName.document].routeName + 'Edit',
                component: () => import('@/views/tabs/documentTabs/Edit.vue'),
                beforeEnter: (to, from, next) => {
                  setViewPort(VIEWPORT_PC)
                  next()
                }
              }
            ]
          },
          {
            path: 'dispensation',
            name: Tabs[TabSettingName.dispensation].routeName,
            component: () => import('@/views/tabs/DispensationTab.vue'),
            props: route => ({
              onLoadShowQr: String(route.query.qr) === 'true',
              defaultRegular: String(route.query.regular) === 'true',
              patientId: Number(route.params.patientId),
              year: route.query.year ? Number(route.query.year) : null
            }),
            beforeEnter: (to, from, next) => {
              setViewPort(VIEWPORT_PC)
              next()
            }
          },
          {
            path: 'dispensation/:dispensationId/check',
            name: 'DispensationCheckTab',
            component: () => import('@/views/tabs/DispensationCheckTab.vue'),
            props: route => ({
              patientId: Number(route.params.patientId),
              dispensationId: Number(route.params.dispensationId)
            }),
            beforeEnter: (to, from, next) => {
              setViewPort(VIEWPORT_PC)
              next()
            }
          },
          {
            path: 'dispensation/create',
            name: 'DispensationCreateTab',
            component: () => import('@/views/tabs/DispensationCreateTab.vue'),
            props: route => ({
              patientId: Number(route.params.patientId)
            }),
            beforeEnter: (to, from, next) => {
              setViewPort(VIEWPORT_PC)
              next()
            }
          },
          {
            path: 'dispensation/calendar',
            name: 'DispensationCalendarTab',
            component: () => import('@/views/tabs/DispensationCalendarTab.vue'),
            props: route => ({
              patientId: Number(route.params.patientId),
              year: route.query.year ? Number(route.query.year) : null
            }),
            beforeEnter: (to, from, next) => {
              setViewPort(VIEWPORT_PC)
              next()
            }
          },
          {
            path: 'prescription',
            name: Tabs[TabSettingName.prescription].routeName,
            component: () => import('@/views/tabs/Prescription.vue'),
            props: route => ({
              onLoadShowQr: String(route.query.qr) === 'true'
            }),
            beforeEnter: (to, from, next) => {
              setViewPort(VIEWPORT_PC)
              next()
            }
          },
          {
            path: 'medicationInstruction',
            name: Tabs[TabSettingName.medicationInstruction].routeName,
            component: () =>
              import('@/views/tabs/MedicationInstructionTab.vue'),
            props: route => ({
              patientId: Number(route.params.patientId)
            }),
            beforeEnter: (to, from, next) => {
              setViewPort(VIEWPORT_SP)
              next()
            }
          },
          {
            path: 'bloodSugarLevel',
            name: Tabs[TabSettingName.bloodSugarLevel].routeName,
            component: () => import('@/views/tabs/BloodSugarLevelTab.vue'),
            props: route => ({
              patientId: Number(route.params.patientId),
              year: route.query.year ? Number(route.query.year) : null,
              month: route.query.month ? Number(route.query.month) : null
            }),
            beforeEnter: (to, from, next) => {
              setViewPort(VIEWPORT_PC)
              next()
            }
          },
          {
            path: 'hd',
            component: () => import('@/views/tabs/HdTab.vue'),
            children: [
              {
                path: '',
                name: Tabs[TabSettingName.hd].routeName,
                component: () => import('@/views/tabs/hdTabs/Index.vue'),
                props: route => ({
                  patientId: Number(route.params.patientId)
                }),
                beforeEnter: (to, from, next) => {
                  setViewPort(VIEWPORT_PC)
                  next()
                }
              },
              {
                path: 'setting',
                name: Tabs[TabSettingName.hd].routeName + 'Setting',
                component: () => import('@/views/tabs/hdTabs/Setting.vue'),
                props: route => ({
                  patientId: Number(route.params.patientId)
                }),
                beforeEnter: (to, from, next) => {
                  setViewPort(VIEWPORT_PC)
                  next()
                }
              }
            ]
          },
          {
            path: 'pd',
            component: () => import('@/views/tabs/PdTab.vue'),
            children: [
              {
                path: '',
                name: Tabs[TabSettingName.pd].routeName,
                component: () => import('@/views/tabs/pd/PdTabIndex.vue'),
                props: route => ({
                  patientId: Number(route.params.patientId)
                }),
                beforeEnter: (to, from, next) => {
                  setViewPort(VIEWPORT_PC)
                  next()
                }
              },
              {
                path: 'create',
                name: 'PdTabCreate',
                component: () => import('@/views/tabs/pd/PdTabCreate.vue'),
                props: route => ({
                  patientId: Number(route.params.patientId)
                }),
                beforeEnter: (to, from, next) => {
                  setViewPort(VIEWPORT_PC)
                  next()
                }
              }
            ]
          }
        ]
      },
      {
        path: 'account/setting',
        name: 'AccountSetting',
        component: () => import('@/views/AccountSetting.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'account/blesetting',
        name: 'BLESetting',
        component: () => import('@/views/BLESetting.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      }
    ]
  },
  {
    path: '/r',
    component: () => import('../views/RelativeBase.vue'),
    beforeEnter: (to, from, next) => {
      setViewPort(VIEWPORT_PC)
      if (
        to.name === 'RelativeCallReceive' ||
        to.name === 'RelativeCallDispatch' ||
        to.name === 'RelativeFileDownload' ||
        to.name === 'ResetPassword' ||
        to.name === 'ResetPassword2'
      ) {
        next()
      }
      const token = getToken()
      if (!token) {
        next({ name: 'PatientLogin' })
        return
      }
      store.commit('user/setTokenToHeader', token)
      next()
    },
    children: [
      {
        path: '',
        name: 'RelativeHome',
        component: () => import('../views/patient/Home.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path:
          'callreceive/' +
          ':token/:refreshToken/:platformId/:roomId/:pin/:secret/:videoEnable/' +
          ':fromuserId/:fromuserPatient/:fromuserLastname/:fromuserFirstname/' +
          ':fromocpCode/:fromocpName/:fromocpSName',
        name: 'RelativeCallReceive',
        component: () => import('../views/VideoCallReceive.vue'),
        props: route => ({
          roomId: String(route.params.roomId),
          pin: String(route.params.pin),
          secret: String(route.params.secret),
          videoEnable: route.params.videoEnable === 'true',
          fromUser: {
            id: Number(route.params.fromuserId),
            isPatient: route.params.fromuserPatient == 'true',
            isWorker: route.params.fromuserPatient == 'false',
            firstName: String(
              decodeURIComponent(route.params.fromuserFirstname)
            ),
            lastName: String(decodeURIComponent(route.params.fromuserLastname))
          },
          fromOccupation: {
            code: String(route.params.fromocpCode),
            name: String(decodeURIComponent(route.params.fromocpName)),
            shortName: String(decodeURIComponent(route.params.fromocpSName))
          }
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          store.commit('user/login', { token: token, refresh: refreshToken })
          const platformId = String(to.params.platformId) as Platform
          setCordova({ platformId })
          next()
        }
      },
      {
        path:
          'calldispatch/:token/:refreshToken/:platformId/:videoApiSecret/:toUsers',
        name: 'RelativeCallDispatch',
        component: () => import('../views/VideoCallDispatch.vue'),
        props: route => ({
          roomId: String(route.params.roomId),
          pin: String(route.params.pin),
          secret: String(route.params.secret),
          videoApiSecret: String(route.params.videoApiSecret),
          toUsers: JSON.parse(decodeURIComponent(String(route.params.toUsers)))
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          store.commit('user/login', { token: token, refresh: refreshToken })
          const platformId = String(to.params.platformId) as Platform
          setCordova({ platformId })
          next()
        }
      },
      {
        path:
          'filedownload/' +
          ':token/:refreshToken/:platformId/' +
          ':type/:patientId/:fileId/:fileName',
        name: 'RelativeFileDownload',
        component: () => import('../views/FileDownload.vue'),
        props: route => ({
          type: String(route.params.type),
          patientId: Number(route.params.patientId),
          fileId: Number(route.params.fileId),
          fileName: String(route.params.fileName)
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          store.commit('user/login', { token: token, refresh: refreshToken })
          const platformId = String(to.params.platformId) as Platform
          setCordova({ platformId })
          next()
        }
      },
      {
        path: 'initial_password_change',
        name: 'InitialRelativePasswordChange',
        component: () => import('../views/patient/InitialPasswordChange.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'password_change',
        name: 'RelativePasswordChange',
        component: () => import('../views/patient/PasswordChange.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'ecg',
        name: 'RelativeECG',
        component: () => import('../views/patient/ECG.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'call',
        name: 'RelativeCall',
        component: () => import('../views/patient/Call.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'message',
        name: 'RelativeMessage',
        component: () => import('../views/patient/Message.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'image',
        name: 'RelativeImageList',
        component: () => import('../views/patient/image/Index.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'hd',
        name: 'RelativeHd',
        component: () => import('../views/patient/Hd.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'profile/edit',
        name: 'RelativeProfileEdit',
        component: () => import('../views/patient/RelativeProfileEdit.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'profile/confirm',
        name: 'RelativeProfileConfirm',
        component: () => import('../views/patient/RelativeProfileConfirm.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'vitalsign',
        name: 'RelativeVitalsign',
        component: () => import('../views/patient/Vitalsign.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        },
        props: route => ({
          patientId: Number(route.params.patientId),
          year: route.query.year ? Number(route.query.year) : null,
          month: route.query.month ? Number(route.query.month) : null,
          day: route.query.day ? Number(route.query.day) : null,
          span: route.query.span ? String(route.query.span) : null,
          before: route.query.before ? String(route.query.before) : 'true'
        })
      },
      {
        path: 'glucose',
        name: 'RelativeGlucose',
        component: () => import('../views/patient/Glucose.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        },
        props: route => ({
          patientId: Number(route.params.patientId),
          year: route.query.year ? Number(route.query.year) : null,
          month: route.query.month ? Number(route.query.month) : null
        })
      },
      {
        path: 'document',
        name: 'RelativeDocumentList',
        component: () => import('../views/patient/document/Index.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'dispensation/calendar',
        name: 'RelativeDispensationCalendar',
        component: () => import('../views/patient/DispensationCalendar.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        },
        props: route => ({
          year: route.query.year ? Number(route.query.year) : null
        })
      }
    ]
  },
  {
    path: '',
    component: () => import('../views/patient/Base.vue'),
    beforeEnter: (to, from, next) => {
      setViewPort(VIEWPORT_SP)
      if (
        to.name === 'PatientQR' ||
        to.name === 'PatientFileDownload' ||
        to.name === 'PatientCallReceive' ||
        to.name === 'PatientCallDispatch' ||
        to.name === 'PatientImageCreateInapp' ||
        to.name === 'PdCreateInapp' ||
        to.name === 'ResetPassword' ||
        to.name === 'ResetPassword2'
      ) {
        next()
      }
      const token = getToken()
      if (!token) {
        next({ name: 'PatientLogin' })
        return
      }
      store.commit('user/setTokenToHeader', token)
      next()
    },

    children: [
      {
        path: '',
        name: 'PatientHome',
        component: () => import('../views/patient/Home.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'hangup-inner',
        name: 'PatientHangupInner',
        component: () => import('../views/VideoCallHangupInner.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path:
          'filedownload/' +
          ':token/:refreshToken/:platformId/' +
          ':type/:patientId/:fileId/:fileName',
        name: 'PatientFileDownload',
        component: () => import('../views/FileDownload.vue'),
        props: route => ({
          type: String(route.params.type),
          patientId: Number(route.params.patientId),
          fileId: Number(route.params.fileId),
          fileName: String(route.params.fileName)
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          store.commit('user/login', { token: token, refresh: refreshToken })
          const platformId = String(to.params.platformId) as Platform
          setCordova({ platformId })
          next()
        }
      },
      {
        path: 'qr/:token/:refreshToken/:platformId',
        name: 'PatientQR',
        component: () => import('../views/patient/Home.vue'),
        props: route => ({
          onLoadShowQr: true
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          store.commit('user/login', { token: token, refresh: refreshToken })
          const platformId = String(to.params.platformId) as Platform
          setCordova({ platformId })
          next()
        }
      },
      {
        path:
          'callreceive/' +
          ':token/:refreshToken/:platformId/:roomId/:pin/:secret/:videoEnable/' +
          ':fromuserId/:fromuserPatient/:fromuserLastname/:fromuserFirstname/' +
          ':fromocpCode/:fromocpName/:fromocpSName',
        name: 'PatientCallReceive',
        component: () => import('../views/VideoCallReceive.vue'),
        props: route => ({
          roomId: String(route.params.roomId),
          pin: String(route.params.pin),
          secret: String(route.params.secret),
          videoEnable: route.params.videoEnable === 'true',
          fromUser: {
            id: Number(route.params.fromuserId),
            isPatient: route.params.fromuserPatient == 'true',
            isWorker: route.params.fromuserPatient == 'false',
            firstName: String(
              decodeURIComponent(route.params.fromuserFirstname)
            ),
            lastName: String(decodeURIComponent(route.params.fromuserLastname))
          },
          fromOccupation: {
            code: String(route.params.fromocpCode),
            name: String(decodeURIComponent(route.params.fromocpName)),
            shortName: String(decodeURIComponent(route.params.fromocpSName))
          }
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          store.commit('user/login', { token: token, refresh: refreshToken })
          const platformId = String(to.params.platformId) as Platform
          setCordova({ platformId })
          next()
        }
      },
      {
        path:
          'calldispatch/:token/:refreshToken/:platformId/:videoApiSecret/:toUsers',
        name: 'PatientCallDispatch',
        component: () => import('../views/VideoCallDispatch.vue'),
        props: route => ({
          roomId: String(route.params.roomId),
          pin: String(route.params.pin),
          secret: String(route.params.secret),
          videoApiSecret: String(route.params.videoApiSecret),
          toUsers: JSON.parse(decodeURIComponent(String(route.params.toUsers)))
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          store.commit('user/login', { token: token, refresh: refreshToken })
          const platformId = String(to.params.platformId) as Platform
          setCordova({ platformId })
          next()
        }
      },
      {
        path: 'initial_password_change',
        name: 'InitialPatientPasswordChange',
        component: () => import('../views/patient/InitialPasswordChange.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'password_change',
        name: 'PatientPasswordChange',
        component: () => import('../views/patient/PasswordChange.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'ecg',
        name: 'PatientECG',
        component: () => import('../views/patient/ECG.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'call',
        name: 'PatientCall',
        component: () => import('../views/patient/Call.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'message',
        name: 'PatientMessage',
        component: () => import('../views/patient/Message.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'pd_create',
        name: 'PdCreate',
        component: () => import('../views/patient/PdCreate.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'pd_create_inapp/:patientId/:token/:refreshToken/:platformId/',
        name: 'PdCreateInapp',
        component: () => import('../views/patient/PdCreateInapp.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          store.commit('user/login', { token: token, refresh: refreshToken })
          const platformId = String(to.params.platformId) as Platform
          setCordova({ platformId })
          next()
        }
      },
      {
        path: 'image',
        name: 'PatientImageList',
        component: () => import('../views/patient/image/Index.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'image/create/inapp/:token/:refreshToken/:platformId/',
        name: 'PatientImageCreateInapp',
        component: () => import('../views/patient/image/UploadInapp.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          const token = String(to.params.token)
          const refreshToken = String(to.params.refreshToken)
          store.commit('user/login', { token: token, refresh: refreshToken })
          const platformId = String(to.params.platformId) as Platform
          setCordova({ platformId })
          next()
        }
      },
      {
        path: 'image/create',
        name: 'PatientImageCreate',
        component: () => import('../views/patient/image/Upload.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'image/edit/:imageId(\\d+)',
        name: 'PatientImageEdit',
        component: () => import('../views/patient/image/Upload.vue'),
        props: route => ({
          imageId: Number(route.params.imageId)
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'hd',
        name: 'PatientHd',
        component: () => import('../views/patient/Hd.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_PC)
          next()
        }
      },
      {
        path: 'profile/edit',
        name: 'PatientProfileEdit',
        component: () => import('../views/patient/PatientProfileEdit.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'profile/confirm',
        name: 'PatientProfileConfirm',
        component: () => import('../views/patient/PatientProfileConfirm.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'vitalsign',
        name: 'PatientVitalsign',
        component: () => import('../views/patient/Vitalsign.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        },
        props: route => ({
          patientId: Number(route.params.patientId),
          year: route.query.year ? Number(route.query.year) : null,
          month: route.query.month ? Number(route.query.month) : null,
          day: route.query.day ? Number(route.query.day) : null,
          span: route.query.span ? String(route.query.span) : null,
          before: route.query.before ? String(route.query.before) : 'true'
        })
      },
      {
        path: 'glucose',
        name: 'PatientGlucose',
        component: () => import('../views/patient/Glucose.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        },
        props: route => ({
          patientId: Number(route.params.patientId),
          year: route.query.year ? Number(route.query.year) : null,
          month: route.query.month ? Number(route.query.month) : null
        })
      },
      {
        path: 'document',
        name: 'PatientDocumentList',
        component: () => import('../views/patient/document/Index.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'document/upload',
        name: 'PatientDocumentUpload',
        component: () => import('../views/patient/document/Upload.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'document/edit/:documentId',
        name: 'PatientDocumentEdit',
        component: () => import('../views/patient/document/Edit.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        },
        props: route => ({
          documentId: route.query.documentId
            ? Number(route.query.documentId)
            : null
        })
      },
      {
        path: 'dispensation/calendar',
        name: 'PatientDispensationCalendar',
        component: () => import('../views/patient/DispensationCalendar.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        },
        props: route => ({
          year: route.query.year ? Number(route.query.year) : null
        })
      },
      {
        path: 'ble/setting/connection',
        name: 'BleSettingConnection',
        component: () => import('../views/patient/bleSetting/Connection.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'relative/',
        name: 'PatientRelative',
        component: () => import('../views/patient/relative/Index.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'relative/add/',
        name: 'PatientRelativeAdd',
        component: () => import('../views/patient/relative/Add.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'relative/add/confirm/',
        name: 'PatientRelativeConfirm',
        component: () => import('../views/patient/relative/Confirm.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'relative/add/complete/',
        name: 'PatientRelativeComplete',
        component: () => import('../views/patient/relative/Complete.vue'),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      },
      {
        path: 'invitation/:invitationId/:hash',
        name: 'PatientInvitation',
        component: () => import('../views/patient/Invitation.vue'),
        props: route => ({
          invitationId: Number(route.params.invitationId),
          hash: route.params.hash
        }),
        beforeEnter: (to, from, next) => {
          setViewPort(VIEWPORT_SP)
          next()
        }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          resolve(savedPosition)
        }, 500)
      })
    } else {
      return { x: 0, y: 0 }
    }
  }
})

export default router
